import axios, { AxiosRequestConfig } from "axios";

import { clearSession, getUserInfo, redirectUrlLogin } from "../common";
import messages from "../messages/ja.json";

// Backend base URL from the env (see .env.*). When empty (local dev) axios uses relative
// paths, which src/setupProxy.js proxies; deploy builds must set REACT_APP_API_URL.
const apiUrl = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: apiUrl,
});

// Error code used for a request that never reached the backend, so callers can tell an
// offline failure from a real backend rejection.
export const NETWORK_ERROR_CODE = "network_error";

export interface ApiError {
  error: { code: string; message: string };
}

export function isNetworkError(err: any): boolean {
  return err?.error?.code === NETWORK_ERROR_CODE;
}

// A request that never got a response (offline, DNS/TLS failure, server unreachable,
// timeout) carries no backend body — axios only gives us the English "Network Error".
// Normalize it to the backend error shape { error: { code, message } } so every page's
// existing `err?.error?.message` read shows the Japanese network message.
function networkError(): ApiError {
  return { error: { code: NETWORK_ERROR_CODE, message: messages.errors.network } };
}

// Response interceptor: a 401 on a normal API call means the session expired -> clear
// it and redirect to login. Auth endpoints (/v1/auth/*) are excluded: their 401 means
// "wrong credentials / invalid session" and must surface inline on the page (login,
// change-password, checkin verify-mfa) so the user can read the error and retry.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // No response at all -> the backend was never reached. Nothing to inspect for a 401,
    // and the session must be kept: losing connectivity is not an expired session.
    if (!axios.isCancel(error) && !error?.response) {
      return Promise.reject(networkError());
    }
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    const isAuthEndpoint = url.includes("/auth/");
    if (status === 401 && !isAuthEndpoint && getUserInfo() !== null) {
      clearSession();
      redirectUrlLogin();
    }
    // Return the backend error body (shape { error: { code, message } }) when present.
    return Promise.reject(error?.response?.data ?? error);
  }
);

const createHeaders = (): Record<string, string> => {
  return {
    "Content-Type": "application/json; charset=UTF-8",
  };
};

export function apiGet<T = unknown>(path: string, params?: unknown): Promise<T> {
  return api
    .get<T>(path, { params, headers: createHeaders() })
    .then((res) => res.data);
}

export function apiPost<T = unknown>(path: string, data?: unknown): Promise<T> {
  return api
    .post<T>(path, data, { headers: createHeaders() })
    .then((res) => res.data);
}

export function apiPut<T = unknown>(path: string, data?: unknown): Promise<T> {
  return api
    .put<T>(path, data, { headers: createHeaders() })
    .then((res) => res.data);
}

export function apiPatch<T = unknown>(path: string, data?: unknown): Promise<T> {
  return api
    .patch<T>(path, data, { headers: createHeaders() })
    .then((res) => res.data);
}

export function apiDelete<T = unknown>(path: string, data?: unknown): Promise<T> {
  const config: AxiosRequestConfig = { headers: createHeaders() };
  if (data) config.data = data;
  return api.delete<T>(path, config).then((res) => res.data);
}

export default api;
